/**
 * AIVerificationService - Centralized AI idea verification logic
 * 
 * Handles communication with Firebase Cloud Functions for idea verification,
 * including spam detection, bot detection, and content approval via Genkit AI.
 * App Check provides automatic security verification on both client and backend.
 */

import errorHandler from '../handlers/ErrorHandler.js';
import CloudFunctionsClient from '../clients/CloudFunctionsClient.js';
import { AI_VERIFICATION } from '../constants/index.js';

class AIVerificationService {
    // Maximum time to wait for verification to complete (15 seconds)
    static VERIFICATION_TIMEOUT = 15000;
    
    // Supported language codes for verification
    static SUPPORTED_LANGS = ['en', 'pl', 'es', 'fr'];
    
    // Input constraints for validation
    static INPUT_LIMITS = {
        TITLE_MIN: 1,
        TITLE_MAX: 200,
        DESCRIPTION_MIN: 50, // Increased from 10 to 50
        DESCRIPTION_MAX: 5000
    };
    
    // Retry configuration for transient errors
    static RETRY_CONFIG = {
        MAX_ATTEMPTS: 3,
        BASE_DELAY: 500,           // 500ms initial delay
        MAX_DELAY: 3000,           // 3 second max delay
        BACKOFF_MULTIPLIER: 2      // Exponential backoff: 500ms, 1s, 2s (capped at 3s)
    };
    
    // Rejection severity levels for user-facing feedback
    static REJECTION_SEVERITY = {
        INFO: 'info',              // No rejection, all checks passed
        WARNING: 'warning',        // Likely spam or bot-generated, but not definitive
        ERROR: 'error'             // AI rejection, content not approved
    };

    /**
     * Attempt verification with exponential backoff retry logic
     * Handles transient network/server failures gracefully
     * 
     * @private
     * @param {string} title - Validated idea title
     * @param {string} description - Validated idea description
     * @param {string} lang - Validated language code
     * @param {string} region - Safe region/hostname
     * @returns {Promise<Object>} Verification result from Cloud Function
     * @throws {Error} If all retry attempts fail or non-transient error occurs
     */
    async #retryVerification(title, description, lang, region) {
        const { MAX_ATTEMPTS, BASE_DELAY, MAX_DELAY, BACKOFF_MULTIPLIER } = AIVerificationService.RETRY_CONFIG;
        let lastError;
        
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                // Attempt the verification call
                return await CloudFunctionsClient.call('verifyIdea', {
                    title,
                    description,
                    lang,
                    region
                });
            } catch (err) {
                lastError = err;
                const errorMsg = err.message.toLowerCase();
                
                // Don't retry on non-transient errors (validation, auth, etc.)
                const isNonTransient = errorMsg.includes('validation') || 
                                      errorMsg.includes('auth') || 
                                      errorMsg.includes('unauthorized') ||
                                      errorMsg.includes('forbidden');
                
                if (isNonTransient) {
                    // Fail immediately on non-transient errors
                    throw err;
                }
                
                // Only retry if attempts remain
                if (attempt < MAX_ATTEMPTS) {
                    // Calculate exponential backoff delay: 500ms, 1s, 2s (capped at 3s)
                    const delayMs = Math.min(
                        BASE_DELAY * Math.pow(BACKOFF_MULTIPLIER, attempt - 1),
                        MAX_DELAY
                    );
                    
                    console.warn(
                        `[AIVerificationService] Verification attempt ${attempt}/${MAX_ATTEMPTS} failed (${err.message}), ` +
                        `retrying in ${delayMs}ms`
                    );
                    
                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }
            }
        }
        
        // All retry attempts exhausted
        throw new Error(
            `AI verification failed after ${MAX_ATTEMPTS} attempts: ${lastError?.message || 'Unknown error'}`
        );
    }

    /**
     * Validate input parameters before sending to backend
     * Performs type checking, length validation, and language validation
     * 
     * @private
     * @param {string} title - Idea title
     * @param {string} description - Idea description
     * @param {string} lang - Language code
     * @returns {Object} Validated and cleaned inputs: { title, description, lang }
     * @throws {Error} If validation fails with detailed error messages
     */
    #validateInput(title, description, lang) {
        const errors = [];

        // Validate title
        if (!title || typeof title !== 'string') {
            errors.push('Title is required and must be a string');
        } else if (title.trim().length === 0) {
            errors.push('Title cannot be empty');
        } else if (title.length > AIVerificationService.INPUT_LIMITS.TITLE_MAX) {
            errors.push(`Title must be under ${AIVerificationService.INPUT_LIMITS.TITLE_MAX} characters (currently ${title.length})`);
        }

        // Validate description
        if (!description || typeof description !== 'string') {
            errors.push(window.Engryv?.i18n?.t('ideaForm.validation.descriptionRequired') || 'Description is required and must be a string');
        } else if (description.trim().length < AIVerificationService.INPUT_LIMITS.DESCRIPTION_MIN) {
            const min = AIVerificationService.INPUT_LIMITS.DESCRIPTION_MIN;
            const current = description.trim().length;
            let msg = window.Engryv?.i18n?.t('ideaForm.validation.descriptionMin') || `Description must be at least ${min} characters (currently ${current}).`;
            msg = msg.replace('{{min}}', min).replace('{{current}}', current);
            errors.push(msg);
        } else if (description.length > AIVerificationService.INPUT_LIMITS.DESCRIPTION_MAX) {
            const max = AIVerificationService.INPUT_LIMITS.DESCRIPTION_MAX;
            const current = description.length;
            let msg = window.Engryv?.i18n?.t('ideaForm.validation.descriptionMax') || `Description must be under ${max} characters (currently ${current}).`;
            msg = msg.replace('{{max}}', max).replace('{{current}}', current);
            errors.push(msg);
        }

        // Validate language
        if (typeof lang !== 'string') {
            console.warn('[AIVerificationService] Invalid language type, defaulting to en');
            lang = 'en';
        } else if (!AIVerificationService.SUPPORTED_LANGS.includes(lang)) {
            console.warn(`[AIVerificationService] Unsupported language: ${lang}, defaulting to en`);
            lang = 'en';
        }

        // If any errors found, throw with all messages
        if (errors.length > 0) {
            throw new Error('Input validation failed: ' + errors.join('; '));
        }

        // Return cleaned/trimmed data
        return {
            title: title.trim(),
            description: description.trim(),
            lang
        };
    }

    /**
     * Determine safe region for verification context
     * Handles edge cases where window.location may not be available
     * 
     * @private
     * @param {string} region - Provided region (optional)
     * @returns {string} Safe region value with fallback chain
     */
    #getRegion(region) {
        // Check if provided region is valid
        if (region && typeof region === 'string' && region.trim()) {
            return region.trim();
        }

        // Attempt to get hostname from window.location
        try {
            const hostname = window?.location?.hostname;
            if (hostname && typeof hostname === 'string' && hostname.trim()) {
                return hostname;
            }
        } catch (error) {
            // In WebWorker or restricted contexts, window.location may throw
            console.warn('[AIVerificationService] Could not access window.location.hostname:', error);
        }

        // Fallback to generic region if all else fails
        console.warn('[AIVerificationService] Region unavailable, using default fallback');
        return 'unknown';
    }

    /**
     * Verify an idea using Firebase Cloud Functions and Genkit AI
     * 
     * @param {string} title - Idea title
     * @param {string} description - Idea description
     * @param {string} lang - Language code (e.g., 'en', 'pl')
     * @param {string} region - Region/hostname for context
     * @returns {Promise<Object>} Verification result with structure:
     *   {
     *     approved: boolean,
     *     feedback: string,
     *     spamScore: number,
     *     isBotGenerated: boolean
     *   }
     * @throws {Error} If verification fails (network, auth, or Cloud Function error)
     */
    async verify(title, description, lang = 'en', region = '') {
        try {
            // Validate inputs early (before timeout/network call)
            const { title: cleanTitle, description: cleanDesc, lang: validLang } = 
                this.#validateInput(title, description, lang);

            // Create timeout promise that rejects after VERIFICATION_TIMEOUT
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => {
                    reject(new Error('AI verification request timed out after ' + AIVerificationService.VERIFICATION_TIMEOUT + 'ms'));
                }, AIVerificationService.VERIFICATION_TIMEOUT);
            });

            // Determine safe region for API call using robust fallback logic
            const safeRegion = this.#getRegion(region);

            // Race between verification (with retry) and timeout
            // Retries handle transient failures, timeout prevents indefinite hangs
            const aiData = await Promise.race([
                this.#retryVerification(cleanTitle, cleanDesc, validLang, safeRegion),
                timeoutPromise
            ]);

            // Validate response structure
            if (!aiData || typeof aiData.approved !== 'boolean') {
                throw new Error('Invalid response from AI verification service');
            }

            return aiData;

        } catch (err) {
            // Distinguish different error types for better logging
            const isTimeout = err.message.includes('timed out');
            const isValidationError = err.message.includes('Input validation');
            const isRetryExhausted = err.message.includes('failed after');
            const logContext = {
                operation: 'AIVerificationService.verify',
                timeout: isTimeout,
                validationError: isValidationError,
                retryExhausted: isRetryExhausted,
                timeoutDuration: AIVerificationService.VERIFICATION_TIMEOUT
            };
            
            if (isValidationError) {
                // Log validation errors as warnings since they're client-side
                errorHandler.handle(
                    err,
                    logContext,
                    'warn'
                );
            } else if (isTimeout) {
                // Log timeout as warning since it's a transient issue
                errorHandler.handle(
                    new Error('AI verification took too long. Please try again.'),
                    logContext,
                    'warn'
                );
            } else if (isRetryExhausted) {
                // Log retry exhaustion as warning (transient issues)
                errorHandler.handle(
                    new Error('Verification service temporarily unavailable. Please try again.'),
                    logContext,
                    'warn'
                );
            } else {
                // Log other errors as regular errors
                errorHandler.handle(err, logContext, 'error');
            }
            
            // Re-throw with more context for caller to handle
            throw err;
        }
    }

    /**
     * Determine if an idea should be rejected based on verification results
     * 
     * @param {Object} verificationResult - Result from verify()
     * @returns {Object} Rejection info: { rejected: boolean, reason: string, severity: 'warning' | 'error' }
     */
    checkRejectionReasons(verificationResult) {
        const { approved, spamScore, isBotGenerated } = verificationResult;

        // Check for spam (high score or bot-generated)
        if (spamScore > AI_VERIFICATION.SPAM_SCORE_THRESHOLD || isBotGenerated) {
            return {
                rejected: true,
                reason: 'Your submission looks a bit like spam. Please make it more specific.',
                severity: AIVerificationService.REJECTION_SEVERITY.WARNING
            };
        }

        // Check if AI rejected it
        if (!approved) {
            return {
                rejected: true,
                reason: verificationResult.feedback || 'Your idea was not approved. Please revise and try again.',
                severity: AIVerificationService.REJECTION_SEVERITY.ERROR
            };
        }

        // All checks passed
        return {
            rejected: false,
            reason: '',
            severity: AIVerificationService.REJECTION_SEVERITY.INFO
        };
    }
}

// Create singleton instance
const aiVerificationService = new AIVerificationService();

export default aiVerificationService;
